import { useState } from "react";
import { MAP_H, MAP_W, REGION_SHAPES } from "@/lib/cameroonMap";
import { fmt } from "@/admin/adminApi";

const MODES = {
  approved: { label: "Vérifiés", rgb: "245, 163, 10", show: (n) => n },
  games: { label: "Parties jouées", rgb: "16, 185, 129", show: (n) => n },
  staked: { label: "Mises", rgb: "56, 189, 248", show: (n) => (n >= 1000 ? `${(n / 1000).toFixed(n < 10000 ? 1 : 0).replace(".0", "")}k` : n) },
};

const shade = (n, max, rgb) => (n ? `rgba(${rgb}, ${(0.35 + 0.65 * (n / max)).toFixed(2)})` : "#1f2633");

const Pills = ({ items, value, onChange, prefix }) => (
  <div className="flex flex-wrap gap-1.5">
    {Object.entries(items).map(([k, l]) => (
      <button key={k} type="button" onClick={() => onChange(k)} data-testid={`${prefix}-${k}`}
        className={`h-8 rounded-full px-3 text-xs font-semibold transition-colors ${value === k ? "bg-amber-500 text-[#0B0D12]" : "border border-white/10 text-slate-300"}`}>{l}</button>
    ))}
  </div>
);

export const CameroonMap = ({ regions, selected, onSelect, period, setPeriod }) => {
  const [hover, setHover] = useState(null);
  const [mode, setMode] = useState("approved");
  const m = MODES[mode];
  const by = Object.fromEntries(regions.filter((r) => r.region).map((r) => [r.region, r]));
  const max = Math.max(1, ...regions.filter((r) => r.region).map((r) => r[mode]));
  const shown = hover || selected;
  const info = shown && by[shown];
  return (
    <div className="relative space-y-2" data-testid="kyc-map">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Pills items={Object.fromEntries(Object.entries(MODES).map(([k, v]) => [k, v.label]))} value={mode} onChange={setMode} prefix="kyc-map-mode" />
        {mode !== "approved" && <Pills items={{ week: "7 jours", month: "30 jours", all: "Tout" }} value={period} onChange={setPeriod} prefix="kyc-map-period" />}
      </div>
      <svg viewBox={`-6 -6 ${MAP_W + 12} ${MAP_H + 12}`} className="mx-auto h-auto w-full max-w-[440px]">
        {REGION_SHAPES.map((s) => (
          <path key={s.region} d={s.d} fill={shade(by[s.region]?.[mode] || 0, max, m.rgb)} stroke="#0E131B" strokeWidth={1.5} strokeLinejoin="round"
            className="cursor-pointer transition-[fill,opacity] duration-300 hover:opacity-80" onClick={() => onSelect(s.region)}
            onPointerEnter={(e) => { if (e.pointerType === "mouse") setHover(s.region); }} onPointerLeave={() => setHover(null)} data-testid={`kyc-map-${s.region}`} />
        ))}
        {selected && REGION_SHAPES.filter((s) => s.region === selected).map((s) => (
          <path key="sel" d={s.d} fill="none" stroke="#fff" strokeWidth="3" strokeLinejoin="round" className="pointer-events-none" data-testid="kyc-map-selected" />
        ))}
        {REGION_SHAPES.map((s) => (
          <g key={`l${s.region}`} className="pointer-events-none" transform={`translate(${s.cx},${s.cy})`}>
            <text textAnchor="middle" y="-4" fontSize="11" fontWeight="600" fill="#F4F6F8" stroke="#0E131B" strokeWidth="3" paintOrder="stroke">{s.region}</text>
            <text textAnchor="middle" y="12" fontSize="13" fontWeight="800" fill="#F4F6F8" stroke="#0E131B" strokeWidth="3" paintOrder="stroke" data-testid={`kyc-map-value-${s.region}`}>{m.show(by[s.region]?.[mode] || 0)}</text>
          </g>
        ))}
      </svg>
      {info && (
        <div className="mx-auto max-w-[440px] rounded-xl border border-white/10 bg-[#121620]/95 px-3 py-2 text-xs" data-testid="kyc-map-tooltip">
          <p className="font-semibold">{shown}</p>
          <p className="text-emerald-400">{info.approved} confirmée(s) · {info.pending} en attente · {info.rejected} refusée(s)</p>
          <p className="text-slate-300">{info.active} joueur(s) actif(s) · {info.games} partie(s) · {fmt(info.staked)} misés</p>
        </div>
      )}
      <div className="flex items-center justify-center gap-2 text-[11px] text-slate-400" data-testid="kyc-map-legend">
        <span className="h-3 w-3 rounded-sm bg-[#1f2633]" /> 0
        <span className="h-3 w-8 rounded-sm" style={{ background: `linear-gradient(90deg, rgba(${m.rgb},0.35), rgba(${m.rgb},1))` }} />
        {mode === "staked" ? fmt(max) : max} {mode === "approved" ? "vérifiés" : mode === "games" ? "parties" : ""}
      </div>
      {mode !== "approved" && <p className="text-center text-[11px] text-slate-500">Parties terminées de Dés, Ludo et Dames, comptées pour chaque joueur selon sa région déclarée.</p>}
    </div>
  );
};
